import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import badMutable from 'dayjs/plugin/badMutable';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(objectSupport);
dayjs.extend(badMutable);
dayjs.extend(weekOfYear);

const localeLoaders: Record<string, () => Promise<unknown>> = {
  cs: () => import(/* webpackChunkName: "dayjs-cs" */ 'dayjs/locale/cs'),
  da: () => import(/* webpackChunkName: "dayjs-da" */ 'dayjs/locale/da'),
  'de-at': () => import(/* webpackChunkName: "dayjs-de-at" */ 'dayjs/locale/de-at'),
  en: () => import(/* webpackChunkName: "dayjs-en" */ 'dayjs/locale/en'),
  es: () => import(/* webpackChunkName: "dayjs-es" */ 'dayjs/locale/es'),
  et: () => import(/* webpackChunkName: "dayjs-et" */ 'dayjs/locale/et'),
  fi: () => import(/* webpackChunkName: "dayjs-fi" */ 'dayjs/locale/fi'),
  fr: () => import(/* webpackChunkName: "dayjs-fr" */ 'dayjs/locale/fr'),
  hu: () => import(/* webpackChunkName: "dayjs-hu" */ 'dayjs/locale/hu'),
  it: () => import(/* webpackChunkName: "dayjs-it" */ 'dayjs/locale/it'),
  lt: () => import(/* webpackChunkName: "dayjs-lt" */ 'dayjs/locale/lt'),
  lv: () => import(/* webpackChunkName: "dayjs-lv" */ 'dayjs/locale/lv'),
  nb: () => import(/* webpackChunkName: "dayjs-nb" */ 'dayjs/locale/nb'),
  nl: () => import(/* webpackChunkName: "dayjs-nl" */ 'dayjs/locale/nl'),
  pl: () => import(/* webpackChunkName: "dayjs-pl" */ 'dayjs/locale/pl'),
  pt: () => import(/* webpackChunkName: "dayjs-pt" */ 'dayjs/locale/pt'),
  ro: () => import(/* webpackChunkName: "dayjs-ro" */ 'dayjs/locale/ro'),
  ru: () => import(/* webpackChunkName: "dayjs-ru" */ 'dayjs/locale/ru'),
  sk: () => import(/* webpackChunkName: "dayjs-sk" */ 'dayjs/locale/sk'),
  sl: () => import(/* webpackChunkName: "dayjs-sl" */ 'dayjs/locale/sl'),
  sv: () => import(/* webpackChunkName: "dayjs-sv" */ 'dayjs/locale/sv'),
  tr: () => import(/* webpackChunkName: "dayjs-tr" */ 'dayjs/locale/tr'),
};

const loadedLocales = new Set<string>();

const normalizeLocale = (locale: string = 'en') => locale.toLowerCase();

export const ensureDayjsLocaleLoaded = async (locale: string = 'en'): Promise<string> => {
  const normalized = normalizeLocale(locale);

  if (loadedLocales.has(normalized)) {
    return normalized;
  }

  const loader = localeLoaders[normalized];

  if (loader) {
    await loader();
    loadedLocales.add(normalized);
    return normalized;
  }

  if (normalized.includes('-')) {
    const baseLocale = normalized.split('-')[0];
    const baseLoader = localeLoaders[baseLocale];

    if (baseLoader) {
      await baseLoader();
      loadedLocales.add(baseLocale);
      return baseLocale;
    }
  }

  const fallbackLocale = 'en';
  const fallbackLoader = localeLoaders[fallbackLocale];
  if (fallbackLoader && !loadedLocales.has(fallbackLocale)) {
    await fallbackLoader();
    loadedLocales.add(fallbackLocale);
  }

  return fallbackLocale;
};

export type { Dayjs };

export default dayjs;
